import { useMemo } from "react";
import { useGetClaimById } from "../../../../queries/billings/claims/use-get-claim-by-id";
import { useGetPatientPaymentsByVisit } from "../../../../queries/billings/payments/use-get-patient-payments-by-visit";
import { useGetCodesForSuperBills } from "../../../../queries/billings/use-get-code-for-super-bill";
import { useProvider } from "../../../../queries/use-provider";
import { getIdFromObj, getXtenantId } from "../../../../utils/utils";
import { ClaimDetails } from "./billing-types";
import { RenderingProviderInfo } from "./claims/updateClaims/PatientDetailsSideSectionForClaim";

// Auto-fetch Place of Service (POS) from the appointment type, matching the
// behaviour on Ready for Billing and Create Claim.
//   VIRTUAL   -> "10 - Telehealth Provided in Patient's Home"
//   IN_PERSON -> "11 - Office"
const mapEncounterModeToPlaceOfService = (
  mode?: string
): string | undefined => {
  if (mode === "VIRTUAL") return "TELEHEALTH_INSIDE_PATIENT_HOME";
  if (mode === "IN_PERSON") return "OFFICE";
  return undefined;
};

const transformClaimToSuperBillFormat = (
  claim?: ClaimDetails,
  derivedPlaceOfService?: string
) => {
  if (!claim) return undefined;
  return {
    patient: claim.patient,
    location: claim.serviceLocation,
    // Prefer the value already stored on the claim; fall back to the POS derived
    // from the encounter's appointment type so it auto-populates.
    placeOfService: claim.placeOfService || derivedPlaceOfService,
    serviceState: claim.state,
    dateOfService: claim.dateOfService,
    renderingProvider: claim.provider,
    priorAuthorization: claim.priorAuthorization,
    billingProvider: claim.billingProvider,
    billingProviderNpi: claim.billingProviderNPI,
    referringProvider: claim.referringProvider,
    referringProviderNpi: claim.renderingProviderNPI,
    orderingProvider: claim.orderingProvider,
    orderingProviderNpi: claim.orderingProviderNPI,
    ecarehealthPatientId: claim.ecarehealthPatientId,
    diagnosisCodes: claim.diagnosisCodes,
    patientProcedureCodes: claim.patientProcedureCodes?.map((code) => {
      // CPT-level patient responsibility breakdown from the posted ERA.
      const copay = Number(code.copay) || 0;
      const coInsurance = Number(code.coInsurance) || 0;
      const deductible = Number(code.deductible) || 0;
      return {
        procedureCode: code.procedureCode,
        // Code type (CPT / HCPCS) drives the label chip on each CPT card.
        type: code.type,
        copay: code.copay,
        coInsurance: code.coInsurance,
        deductible: code.deductible,
        // CPT Total must reflect the ERA breakdown (Copay + Coinsurance +
        // Deductible), not the full procedure charge.
        total: (copay + coInsurance + deductible).toFixed(2),
        active: code.active,
        archive: code.archive,
      };
    }),
    total: claim.patientResponsibility,
    subTotal: claim.patientResponsibility,
    // Insurance Amount to be submit comes from the posted ERA on the claim.
    // Informational only for PR invoices (not subtracted again).
    insuranceAmount: claim.paidAmount,
    // Adv Paid + Prev. Balance are pulled from the consolidated
    // patient-payment by-visit API and merged in below (see billDetails).
    encounter: claim.encounter,
    claimUuid: claim.uuid,
  };
};

interface PrInvoiceFromClaim {
  billDetails: any;
  visitId?: string;
  renderingProviderInfo?: RenderingProviderInfo;
}

/**
 * Everything a Patient Responsibility invoice pre-fills from the claim it
 * settles: the service details / CPT breakdown, the visit behind the claim, the
 * patient balance figures and the rendering provider block.
 *
 * Shared by the two PR entry points so both screens populate identically:
 *  - Claims listing -> "Create Invoice for PR" (claim id comes from the URL)
 *  - Invoice listing -> Create Invoice -> PR (claim id comes from the Claim
 *    dropdown in the header)
 */
export const usePrInvoiceFromClaim = (
  claimUuid?: string,
  patientUuid?: string
): PrInvoiceFromClaim => {
  const { data: claimData } = useGetClaimById({
    claimUuid: claimUuid ?? "",
  });

  // Visit (encounter) id tied to this claim — needed to fetch the
  // appointment-scoped Adv Paid and the patient-wide Prev. Balance.
  const visitId = useMemo(() => {
    const encounter = claimData?.data?.encounter;
    return encounter ? Object.keys(encounter)[0] : undefined;
  }, [claimData?.data]);

  // Consolidated payment summary: advancePayment + previousBalance.
  const { data: paymentByVisit } = useGetPatientPaymentsByVisit({
    patientUuid,
    visitId,
  });

  // Fetch the encounter behind this claim so we can derive POS from its
  // appointment type when the claim itself has no POS stored.
  const { data: encounterData } = useGetCodesForSuperBills({
    appointmentUuid: getIdFromObj(claimData?.data?.encounter) ?? "",
    xTENANTID: getXtenantId(),
  });

  const derivedPlaceOfService = useMemo(
    () => mapEncounterModeToPlaceOfService(encounterData?.data?.mode),
    [encounterData?.data?.mode]
  );

  const billDetails = useMemo(() => {
    const base = transformClaimToSuperBillFormat(
      claimData?.data,
      derivedPlaceOfService
    );
    if (!base) return undefined;
    const payment = paymentByVisit?.data;
    return {
      ...base,
      advancedPaid: payment?.advancePayment,
      previousBalance: payment?.previousBalance,
    };
  }, [claimData?.data, paymentByVisit?.data, derivedPlaceOfService]);

  // Provider id behind this claim — used to fetch the provider record, which is
  // the only source for Taxonomy Number / License TX (the claim does not carry
  // them).
  const renderingProviderId = useMemo(() => {
    const provider = claimData?.data?.provider;
    return provider ? Object.keys(provider)[0] : undefined;
  }, [claimData?.data]);

  const { data: providerRes } = useProvider({
    providerUuid: renderingProviderId,
    tenantId: getXtenantId(),
  });

  const renderingProviderInfo = useMemo(() => {
    const claim = claimData?.data;
    if (!claim?.provider) return undefined;
    const [providerId, providerName] = Object.entries(
      claim.provider as Record<string, string>
    )[0] ?? [undefined, undefined];
    const provider = providerRes?.data;
    return {
      name: claim.renderingProviderName ?? providerName,
      providerId,
      npi: claim.renderingProviderNPI?.toString() ?? undefined,
      licenseNumber:
        claim.renderingProviderLicenseNumber ??
        provider?.licenseNumber ??
        undefined,
      taxonomyNumber: provider?.taxonomyNumber ?? undefined,
    };
  }, [claimData?.data, providerRes?.data]);

  return { billDetails, visitId, renderingProviderInfo };
};
